import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  const articleLinks = [
    { label: 'Complete Guide', path: '/vigorbuy-spreadsheet-complete-guide' },
    { label: 'Platform Comparison', path: '/vigorbuy-spreadsheet-comparison' },
    { label: 'Templates Library', path: '/vigorbuy-spreadsheet-templates' },
    { label: 'In-Depth Review', path: '/vigorbuy-spreadsheet-review' },
    { label: 'Security Guide', path: '/vigorbuy-spreadsheet-security' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 no-underline shrink-0">
          <span className="font-heading text-xl font-bold text-foreground-900 whitespace-nowrap">
            VigorBuy <span className="text-primary-500">Spread</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          <Link to="/" className="text-sm font-semibold text-primary-500 whitespace-nowrap flex items-center gap-1">
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
            Home
          </Link>
          <div className="relative group">
            <button className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
              Articles
              <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
            </button>
            <div className="absolute top-full left-0 mt-2 w-56 py-2 rounded-xl bg-background-50 border border-background-200/70 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              {articleLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
          <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-1">
            <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
            Shopping Guide
          </a>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
          >
            Buy It Now
          </a>
        </div>

        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          onClick={() => { setMobileOpen(!mobileOpen); setMobileExpanded(null); }}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl text-foreground-800`}></i>
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-primary-500 hover:bg-primary-50 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              Home
            </Link>

            <div className="border-t border-background-200/70 my-1"></div>

            <button
              onClick={() => setMobileExpanded(mobileExpanded === 'articles' ? null : 'articles')}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
                SEO Articles
              </span>
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileExpanded === 'articles' ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileExpanded === 'articles' && (
              <div className="ml-4 pl-3 border-l-2 border-background-200/70 flex flex-col gap-1">
                {articleLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <button
              onClick={() => setMobileExpanded(mobileExpanded === 'categories' ? null : 'categories')}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-store-2-line w-4 h-4 flex items-center justify-center"></i>
                Product Categories
              </span>
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${mobileExpanded === 'categories' ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileExpanded === 'categories' && (
              <div className="ml-4 pl-3 border-l-2 border-background-200/70 flex flex-col gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
              Shopping Guide
            </a>

            <div className="border-t border-background-200/70 my-1"></div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mx-3 mt-1 text-center px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}